import React, { useState, useEffect, useRef } from 'react';
import {
  Box, Typography, Stack, IconButton, CircularProgress,
} from '@mui/material';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import ChevronLeftIcon  from '@mui/icons-material/ChevronLeft';
import ClearIcon        from '@mui/icons-material/Clear';
import { HEBREW_MONTH_NAMES, toHebrewYear } from '../../../business/ParnasWidget/parnasConstants';

const navy = '#1e3a5f';

const HEBREW_LETTERS = [
  '', 'א', 'ב', 'ג', 'ד', 'ה', 'ו', 'ז', 'ח', 'ט', 'י',
  'יא', 'יב', 'יג', 'יד', 'טו', 'טז', 'יז', 'יח', 'יט', 'כ',
  'כא', 'כב', 'כג', 'כד', 'כה', 'כו', 'כז', 'כח', 'כט', 'ל',
];
const toHebrewNum = (n) => HEBREW_LETTERS[n] || String(n);

const HMONTH_ENG = ['', 'Nisan', 'Iyyar', 'Sivan', 'Tamuz', 'Av', 'Elul',
                    'Tishrei', 'Cheshvan', 'Kislev', 'Tevet', "Sh'vat", 'Adar', 'Adar II'];

const MONTH_NAME_TO_NUM = {
  'Nisan': 1, 'Iyyar': 2, 'Iyar': 2, 'Sivan': 3, 'Tamuz': 4, 'Tammuz': 4,
  'Av': 5, 'Elul': 6, 'Tishrei': 7, 'Tishri': 7, 'Cheshvan': 8, 'Heshvan': 8,
  'Kislev': 9, 'Tevet': 10, "Sh'vat": 11, 'Shvat': 11,
  'Adar1': 12, 'Adar': 12, 'Adar I': 12, 'Adar2': 13, 'Adar II': 13,
};

const todayISO = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};

// ─── API cache ──────────────────────────────────────────────────────────────
const calendarCache = {};

const fetchMonthData = async (hYear, hMonth) => {
  const key = `${hYear}-${hMonth}`;
  if (calendarCache[key]) return calendarCache[key];

  const url =
    `https://www.hebcal.com/hebcal?v=1&cfg=json` +
    `&year=${hYear}&month=${hMonth}&yt=H` +
    `&d=on&maj=on&min=on&nx=on&ss=on&mf=on&s=on`;

  const res  = await fetch(url);
  const json = await res.json();
  const items = [...(json.items || [])];

  const hasHebdate = items.some(i => i.category === 'hebdate');
  if (!hasHebdate) {
    try {
      const hMonthName = HMONTH_ENG[hMonth] || 'Nisan';
      const convUrl = `https://www.hebcal.com/converter?cfg=json&hy=${hYear}&hm=${encodeURIComponent(hMonthName)}&hd=1&h2g=1`;
      const conv = await (await fetch(convUrl)).json();
      if (conv.gy && conv.gm && conv.gd) {
        const monthLen = conv.monthLen || 30;
        const start = new Date(conv.gy, conv.gm - 1, conv.gd);
        for (let hd = 1; hd <= monthLen; hd++) {
          const gd = new Date(start.getFullYear(), start.getMonth(), start.getDate() + hd - 1);
          const isoDate = `${gd.getFullYear()}-${String(gd.getMonth() + 1).padStart(2, '0')}-${String(gd.getDate()).padStart(2, '0')}`;
          items.push({ category: 'hebdate', date: isoDate, hd, hm: hMonthName, hy: hYear });
        }
      }
    } catch (_) { /* ignore */ }
  }

  calendarCache[key] = items;
  return items;
};

const buildDayMap = (items) => {
  const map = {};
  for (const item of items) {
    const date = item.date?.substring(0, 10);
    if (!date) continue;
    if (!map[date]) map[date] = { gregDate: date, hd: null, events: [], isShabbat: false };
    if (item.category === 'hebdate') {
      const hd = typeof item.hd === 'number' ? item.hd : parseInt(item.title, 10);
      map[date].hd = isNaN(hd) ? null : hd;
    } else if (item.category === 'parashat') {
      map[date].isShabbat = true;
    } else if (['holiday', 'roshchodesh', 'fast'].includes(item.category)) {
      map[date].events.push(item.hebrew || item.title);
    }
  }
  return map;
};

/**
 * HebrewDatePicker
 * בוחר תאריך עברי פשוט — מציג לוח חודש עברי ומחזיר תאריך לועזי (YYYY-MM-DD).
 *
 * Props:
 *   value    — string (YYYY-MM-DD) | '' — תאריך נבחר
 *   onChange — (isoDate: string) => void
 *   label    — string — כותרת (אופציונלי)
 */
const HebrewDatePicker = ({ value = '', onChange, label }) => {
  const [currentHMonth, setCurrentHMonth] = useState(null);
  const [currentHYear,  setCurrentHYear]  = useState(null);
  const [dayMap,   setDayMap]   = useState({});
  const [loading,  setLoading]  = useState(false);
  const [error,    setError]    = useState(null);
  const [selectedHebLabel, setSelectedHebLabel] = useState('');
  const initialized = useRef(false);

  // ─── Init: resolve today's Hebrew month ──────────────────────────────────
  useEffect(() => {
    if (initialized.current) return;
    initialized.current = true;

    // If a value is already set, navigate to that month
    const dateToResolve = value || todayISO();
    fetch(`https://www.hebcal.com/converter?cfg=json&date=${dateToResolve}&g2h=1`)
      .then(r => r.json())
      .then(data => {
        const hm = MONTH_NAME_TO_NUM[data.hm] ?? 7;
        setCurrentHMonth(hm);
        setCurrentHYear(data.hy);
        if (value) {
          setSelectedHebLabel(`${toHebrewNum(data.hd)} ${HEBREW_MONTH_NAMES[hm]} ${toHebrewYear(data.hy)}`);
        }
      })
      .catch(() => { setCurrentHMonth(7); setCurrentHYear(5785); });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // ─── Fetch month data ────────────────────────────────────────────────────
  useEffect(() => {
    if (!currentHMonth || !currentHYear) return;
    const cacheKey = `${currentHYear}-${currentHMonth}`;
    if (calendarCache[cacheKey]) { setDayMap(buildDayMap(calendarCache[cacheKey])); return; }
    setLoading(true);
    setError(null);
    fetchMonthData(currentHYear, currentHMonth)
      .then(items => { setDayMap(buildDayMap(items)); setLoading(false); })
      .catch(() => { setError('שגיאה בטעינת הלוח'); setLoading(false); });
  }, [currentHMonth, currentHYear]);

  const sortedDates = Object.keys(dayMap).filter(d => dayMap[d].hd != null).sort();

  const buildWeeks = () => {
    if (sortedDates.length === 0) return [];
    const weeks = [];
    let week = Array(7).fill(null);
    for (const d of sortedDates) {
      const dow = new Date(d).getDay();
      week[dow] = d;
      if (dow === 6) { weeks.push(week); week = Array(7).fill(null); }
    }
    if (week.some(Boolean)) weeks.push(week);
    return weeks;
  };
  const weeks = buildWeeks();

  const handleNav = async (dir) => {
    if (sortedDates.length === 0) return;
    setLoading(true); setError(null);
    try {
      const d = new Date(sortedDates[0]);
      d.setDate(d.getDate() + (dir === 'next' ? 31 : -1));
      const iso = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
      const conv = await (await fetch(`https://www.hebcal.com/converter?cfg=json&date=${iso}&g2h=1`)).json();
      const newHMonth = MONTH_NAME_TO_NUM[conv.hm];
      if (newHMonth != null && conv.hy) {
        const items = await fetchMonthData(conv.hy, newHMonth);
        setDayMap(buildDayMap(items));
        setCurrentHMonth(newHMonth);
        setCurrentHYear(conv.hy);
      }
    } catch (_) { setError('שגיאה בניווט'); }
    finally { setLoading(false); }
  };

  const handleDayClick = (gregDate) => {
    const day = dayMap[gregDate];
    if (!day) return;
    const hebLabel = `${toHebrewNum(day.hd)} ${HEBREW_MONTH_NAMES[currentHMonth]} ${toHebrewYear(currentHYear)}`;
    setSelectedHebLabel(hebLabel);
    onChange(gregDate);
  };

  const handleClear = () => {
    setSelectedHebLabel('');
    onChange('');
  };

  const DAY_LABELS = ['א׳', 'ב׳', 'ג׳', 'ד׳', 'ה׳', 'ו׳', 'ש׳'];

  if (!currentHMonth) {
    return <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}><CircularProgress size={20} /></Box>;
  }

  return (
    <Box dir="rtl">
      {/* Selected value display */}
      {label && (
        <Typography sx={{ fontSize: '0.8rem', color: '#6b7280', mb: 0.75, fontWeight: 500 }}>{label}</Typography>
      )}

      {value && selectedHebLabel && (
        <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
          <Box sx={{
            px: 1.5, py: 0.5, borderRadius: 1.5,
            bgcolor: '#eff6ff', border: '1px solid #bfdbfe',
            display: 'flex', alignItems: 'center', gap: 1,
          }}>
            <Typography sx={{ fontSize: '0.85rem', fontWeight: 600, color: navy }}>
              {selectedHebLabel}
            </Typography>
            <Typography sx={{ fontSize: '0.72rem', color: '#6b7280' }}>
              ({value})
            </Typography>
            <IconButton size="small" onClick={handleClear} sx={{ p: 0.25, color: '#9ca3af', '&:hover': { color: '#ef4444' } }}>
              <ClearIcon sx={{ fontSize: 14 }} />
            </IconButton>
          </Box>
        </Stack>
      )}

      {!value && (
        <Typography sx={{ fontSize: '0.72rem', color: '#9ca3af', mb: 1 }}>
          לחץ על יום בלוח כדי לבחור תאריך
        </Typography>
      )}

      {/* Calendar */}
      <Box sx={{ border: '1px solid #e5e7eb', borderRadius: 2, overflow: 'hidden', bgcolor: 'white' }}>

        {/* Header */}
        <Box sx={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          px: 2, py: 1, borderBottom: '1px solid #e5e7eb', bgcolor: '#fafafa',
        }}>
          <IconButton size="small" onClick={() => handleNav('prev')} disabled={loading}>
            <ChevronRightIcon fontSize="small" />
          </IconButton>
          <Typography sx={{ fontWeight: 700, color: navy, fontSize: '0.95rem' }}>
            {HEBREW_MONTH_NAMES[currentHMonth]} {toHebrewYear(currentHYear)}
          </Typography>
          <IconButton size="small" onClick={() => handleNav('next')} disabled={loading}>
            <ChevronLeftIcon fontSize="small" />
          </IconButton>
        </Box>

        {/* Grid */}
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}><CircularProgress size={20} /></Box>
        ) : error ? (
          <Typography sx={{ textAlign: 'center', color: '#ef4444', fontSize: '0.8rem', py: 2 }}>{error}</Typography>
        ) : (
          <Box sx={{ p: 1 }}>
            {/* Day-of-week header */}
            <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: '1px', mb: '2px' }}>
              {DAY_LABELS.map((d, i) => (
                <Typography key={i} sx={{
                  textAlign: 'center', fontSize: '0.72rem', fontWeight: 600,
                  color: i === 6 ? '#b45309' : '#9ca3af', pb: 0.5,
                }}>
                  {d}
                </Typography>
              ))}
            </Box>

            {/* Weeks */}
            {weeks.map((week, wi) => (
              <Box key={wi} sx={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: '1px', mb: '1px' }}>
                {week.map((gregDate, di) => {
                  if (!gregDate) return <Box key={di} sx={{ minHeight: 40 }} />;
                  const day = dayMap[gregDate];
                  const isSel = value === gregDate;
                  const isShabbat = di === 6;
                  const isToday = gregDate === todayISO();
                  const gregDay = new Date(gregDate).getDate();

                  return (
                    <Box
                      key={di}
                      onClick={() => handleDayClick(gregDate)}
                      sx={{
                        minHeight: 40, p: '3px', borderRadius: 1, cursor: 'pointer',
                        border: isSel ? `2px solid ${navy}` : isToday ? '1px solid #93c5fd' : '1px solid #f3f4f6',
                        bgcolor: isSel ? '#eff6ff' : isShabbat ? '#fefce8' : 'white',
                        '&:hover': { bgcolor: '#eff6ff', borderColor: navy },
                        display: 'flex', flexDirection: 'column', gap: '1px',
                      }}
                    >
                      <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
                        <Typography sx={{
                          fontSize: '0.75rem', fontWeight: isSel ? 700 : isToday ? 700 : 500,
                          color: isSel ? navy : isShabbat ? '#b45309' : isToday ? navy : '#374151',
                          lineHeight: 1,
                        }}>
                          {day?.hd ? toHebrewNum(day.hd) : ''}
                        </Typography>
                        <Typography sx={{ fontSize: '0.58rem', color: '#9ca3af', lineHeight: 1 }}>
                          {gregDay}
                        </Typography>
                      </Stack>
                      {day?.events?.slice(0, 1).map((ev, ei) => (
                        <Typography key={ei} sx={{
                          fontSize: '0.5rem', color: '#b45309', lineHeight: 1.1,
                          overflow: 'hidden', whiteSpace: 'nowrap', textOverflow: 'ellipsis',
                        }}>
                          {ev}
                        </Typography>
                      ))}
                    </Box>
                  );
                })}
              </Box>
            ))}
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default HebrewDatePicker;
